import { useCallback, useContext, useSyncExternalStore } from 'react'
import { QueryClientContext } from '@tanstack/react-query'
// From its own module, not `useSchedules.js`: several suites mock that one wholesale.
import { SCHEDULES_QUERY_KEY } from './schedulesQueryKey'

/**
 * The results bucket's age limit in days, as the worker declares it on the schedules
 * list (spec 152) — or null when nothing has said.
 *
 * For the two schedule EDITORS (the Manage Schedules edit modal and the queue modal's
 * schedule section), whose "Keep results" sentence must name that age, and for the
 * Manage Schedules page's own retention panel. The queue modal
 * has four hosts — the Queue page, Manage Schedules, the report page and the specialized
 * filter bar — and only two of them read schedules, so a prop would leave half of them
 * silent about the age, i.e. saying something different for the same schedule.
 *
 * PASSIVE by design: it reads whatever schedules list react-query already holds and
 * never fetches. The header's notification center keeps `['report-schedules', 'mine']`
 * loaded on every page wherever report jobs are on — and the editors exist only there.
 * Every scope's envelope carries the same value, so any cached one will do. With no
 * QueryClient (a bare component test) or no cached list, it answers null, and the
 * sentence then states no age rather than guessing one.
 */
export function useResultMaxAgeDays() {
  const client = useContext(QueryClientContext)
  const subscribe = useCallback(
    (onChange) => (client ? client.getQueryCache().subscribe(onChange) : () => {}),
    [client]
  )
  const read = useCallback(() => {
    if (!client) return null
    for (const [, data] of client.getQueriesData({ queryKey: SCHEDULES_QUERY_KEY })) {
      const d = Number(data?.resultMaxAgeDays)
      if (data?.resultMaxAgeDays != null && Number.isFinite(d) && d > 0) return d
    }
    return null
  }, [client])
  return useSyncExternalStore(subscribe, read, read)
}

export default useResultMaxAgeDays
